import React, { useEffect, useState } from 'react';
import { Clickable, Icon } from '@fashionnovateam/nova-ui-toolkit';
import { PrintRequest, PrintRequestStatus } from '../../../Lib/Api/asn';
import {
  useCancelAllPrintRequests,
  useGetActivePrintRequests,
} from '../../../Lib/Hooks/printRequestStatus.hooks';
import { ActionConfirmationModal } from '../NewDesign/Modals/ActionConfirmationModal';

const PrintRequestStatusToSentence = {
  [PrintRequestStatus.REQUESTED]: 'Processing print request...',
  [PrintRequestStatus.CANCELLED]: 'Print request cancelled.',
  [PrintRequestStatus.SENT_TO_PRINTER_FAIL]: 'Print request failed.',
  [PrintRequestStatus.SENT_TO_PRINTER_OK]:
    'Request successfully sent to the printer.',
  [PrintRequestStatus.SENDING_TO_PRINTER]: 'Sending request to the printer...',
  [PrintRequestStatus.ZPL_GENERATED]: 'ZPL Generated.',
};

const LabelType = {
  unit: 'units',
  hangtag: 'hangtags',
  carton: 'cartons',
};

const PrintRequestStatusToIcon: Record<string, React.ReactNode> = {
  [PrintRequestStatus.REQUESTED]: (
    <Icon className="w-6 h-6 relative" name="loading-circle" />
  ),
  [PrintRequestStatus.SENT_TO_PRINTER_FAIL]: (
    <Icon
      className="w-6 h-6 relative text-danger-700"
      name="alert/alert-circle"
    />
  ),
  [PrintRequestStatus.SENT_TO_PRINTER_OK]: (
    <Icon
      className="w-6 h-6 relative text-success-700"
      name="check/check-tick-circle"
      variant="solid"
    />
  ),
  [PrintRequestStatus.SENDING_TO_PRINTER]: (
    <Icon className="w-6 h-6 relative" name="loading-circle" />
  ),
  [PrintRequestStatus.ZPL_GENERATED]: (
    <Icon className="w-6 h-6 relative" name="loading-circle" />
  ),
};

export interface PrinterRequestsModalHeaderProps {
  isOpen: boolean;
  onCloseConfirm: () => void;
  onCollapse: () => void;
  quantity: number;
}

function PrinterRequestsModalHeader({
  isOpen,
  quantity,
  onCloseConfirm,
  onCollapse,
}: PrinterRequestsModalHeaderProps) {
  const [clearPrintRequestModalOpen, setClearPrintRequestModalOpen] =
    useState(false);

  return (
    <div className="bg-base-primary rounded-t-lg p-2 pl-3 flex text-base-primary-inverse font-semibold text-sm">
      <ActionConfirmationModal
        title="Clear Print Requests"
        description={
          <>
            Print requests sent to printers will not be cancelled.
            <br /> Would you like to clear requests informations?
          </>
        }
        confirmText="Yes, Clear"
        denyText="Cancel"
        open={clearPrintRequestModalOpen}
        onConfirm={() => {
          setClearPrintRequestModalOpen(false);
        }}
        onClose={() => {
          onCloseConfirm();
          setClearPrintRequestModalOpen(false);
        }}
      />
      <div>Print Requests ({quantity})</div>
      <div className="absolute right-3 flex gap-1">
        <Clickable>
          <Icon
            className="w-5 h-5"
            name={isOpen ? 'chevron/chevron-down' : 'chevron/chevron-up'}
            onClick={onCollapse}
          />
        </Clickable>
        <Clickable>
          <Icon
            className="w-5 h-5"
            name="multiple-cross/multiple-cross-cancel-default"
            onClick={() => setClearPrintRequestModalOpen(true)}
          />
        </Clickable>
      </div>
    </div>
  );
}

export interface PrinterRequestsModalItemProps {
  item: PrintRequest;
}

function PrinterRequestsModalItem({ item }: PrinterRequestsModalItemProps) {
  return (
    <div className="pt-4 pb-4 pr-3 pl-3 border-b border-r border-l border-neutral-200 h-16 justify-start items-center gap-3 inline-flex px-5 py-4 w-full">
      <div className="grow shrink flex-col basis-0 justify-center items-start gap-0.5 inline-flex">
        <div className="self-stretch text-black text-[13px] font-medium font-['Geist'] leading-none">{`${item?.purchase_order?.po_number ?? ''} | ${item.sku}`}</div>
        <div className="self-stretch text-base-disabled text-xs font-normal font-['Geist'] leading-none">
          {`${PrintRequestStatusToSentence[item.request_status]}${
            item.request_status === PrintRequestStatus.SENT_TO_PRINTER_FAIL &&
            item.error
              ? ` Response: ${item.error}.`
              : ''
          }`}
        </div>
      </div>
      <div className="px-2 py-1 bg-neutral-100 rounded justify-center items-center gap-1 flex">
        <div className="text-base-disabled text-base-primary text-[11px] font-normal font-['Geist'] leading-none">{`${item.number_of_labels} ${LabelType[item.label_type]}`}</div>
      </div>
      {PrintRequestStatusToIcon[item.request_status]}
    </div>
  );
}

export interface PrinterRequestsModalProps {
  userId: string;
}

function PrinterRequestsModal({ userId }: PrinterRequestsModalProps) {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const { data: activeItems } = useGetActivePrintRequests(userId);
  const cancelAllPrintRequests = useCancelAllPrintRequests();
  const [allRequests, setAllRequests] = useState<PrintRequest[] | null>(null);
  const [requestsDisplayed, setRequestsDisplayed] = useState<
    PrintRequest[] | null
  >(null);

  useEffect(() => {
    const handleBeforeUnload = async (event: BeforeUnloadEvent) => {
      event.stopPropagation();
      event.preventDefault();
    };
    const handlePageHide = async () => {
      cancelAllPrintRequests.mutate(userId);
    };
    window.addEventListener('pagehide', handlePageHide);

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [requestsDisplayed]);

  useEffect(() => {
    if (!activeItems || (activeItems && !activeItems.length)) {
      return;
    }
    const activeItemsCopy = [...activeItems];
    const newRequests = activeItemsCopy.filter((item) => {
      const existingRequest = allRequests?.find(
        (request) => request.request_id === item.request_id,
      );

      if (existingRequest) {
        return existingRequest.request_status !== PrintRequestStatus.CANCELLED;
      }

      return true;
    });

    const newRequestsCopy = [...newRequests];
    const cancelFilteredOut = newRequestsCopy?.filter(
      (request) => request.request_status !== PrintRequestStatus.CANCELLED,
    );

    setAllRequests((prevState) => {
      if (!prevState) return newRequests;
      return [...prevState, ...newRequests];
    });
    setRequestsDisplayed(cancelFilteredOut);
    // If there are active items, show the modal
    setHidden(false);
  }, [activeItems]);

  useEffect(() => {
    if (
      !requestsDisplayed ||
      (requestsDisplayed && !requestsDisplayed.length)
    ) {
      // If there are no active items, close the modal
      setHidden(true);
    } else {
      // If there are active items, show the modal
      setHidden(false);
    }
  }, [requestsDisplayed]);

  const handleClearQueue = () => {
    setAllRequests((prevState) => {
      if (!prevState) return null;
      return prevState.map((item: PrintRequest) => ({
        ...item,
        request_status: PrintRequestStatus.CANCELLED,
      }));
    });
    setHidden(true);
    cancelAllPrintRequests.mutate(userId);
  };

  const handleCollapse = () => {
    setOpen((prevState) => !prevState);
  };

  if (hidden) return null;

  if (!open) {
    return (
      <div className="twp fixed right-0 bottom-0 mr-10 w-[560px] z-40">
        <PrinterRequestsModalHeader
          quantity={requestsDisplayed ? requestsDisplayed.length : 0}
          onCloseConfirm={handleClearQueue}
          onCollapse={handleCollapse}
          isOpen={open}
        />
      </div>
    );
  }

  return (
    <div className="twp fixed right-0 bottom-0 mr-10 w-[560px] bg-base-primary-inverse rounded-t-lg z-40">
      <PrinterRequestsModalHeader
        quantity={requestsDisplayed ? requestsDisplayed.length : 0}
        onCloseConfirm={handleClearQueue}
        onCollapse={handleCollapse}
        isOpen={open}
      />
      <div
        className="max-h-[416px] overflow-y-scroll"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {requestsDisplayed &&
          requestsDisplayed.map((item: PrintRequest) => (
            <PrinterRequestsModalItem key={item.request_id} item={item} />
          ))}
      </div>
    </div>
  );
}

export default PrinterRequestsModal;
